import { PolymorphicPropsWithoutRef } from "@oneleet/utils/src/polymorphism";

import { Dialog } from "@headlessui/react";
import { type VariantProps, cva } from "class-variance-authority";
import { motion } from "framer-motion";
import { twMerge } from "tailwind-merge";

const DEFAULT_SIZE = "md";

const modalSize = cva(
  "relative z-[90] w-full overflow-hidden rounded-lg bg-white px-4 pb-4 pt-5 shadow-xl sm:mx-6 sm:my-8 sm:p-6",
  {
    variants: {
      size: {
        xs: "max-w-xs",
        sm: "max-w-sm",
        md: "max-w-md",
        lg: "max-w-lg",
        xl: "max-w-xl",
        "2xl": "max-w-2xl",
        "3xl": "max-w-3xl",
        "4xl": "max-w-4xl",
      },
    },
    defaultVariants: {
      size: DEFAULT_SIZE,
    },
  },
);

type ModalSizeVariants = VariantProps<typeof modalSize>;

export type ModalProps = {
  children?: React.ReactNode;
  className?: string;
  onClose?: () => void;
} & ModalSizeVariants;

const ANIMATION_ENTER_TIME_SECONDS = 0.05;
const ANIMATION_EXIT_TIME_SECONDS = 0.1;

const Modal = <T extends React.ElementType>({
  as,
  size = DEFAULT_SIZE,
  children,
  className,
  onClose = () => null,
  ...rest
}: PolymorphicPropsWithoutRef<ModalProps, T>) => {
  const Component: React.ElementType = as ?? "div";

  return (
    <Dialog
      as={Component}
      className="relative z-[90]"
      open={true}
      onClose={onClose}
      {...rest}
    >
      <motion.div
        className="fixed inset-0 bg-gray-500"
        initial={{
          opacity: 0,
        }}
        animate={{
          opacity: 0.75,
          transition: {
            duration: ANIMATION_ENTER_TIME_SECONDS,
          },
        }}
        exit={{
          opacity: 0,
          transition: {
            duration: ANIMATION_EXIT_TIME_SECONDS,
          },
        }}
      />
      <div className="fixed inset-0 z-[90] overflow-y-auto">
        <div className="flex min-h-full items-center justify-center p-4 sm:p-2">
          <Dialog.Panel
            as={motion.div}
            className={twMerge(modalSize({ size, className }))}
            initial={{
              opacity: 0,
              scale: 0.95,
            }}
            animate={{
              opacity: 1,
              y: 0,
              scale: 1,
              transition: {
                duration: ANIMATION_ENTER_TIME_SECONDS,
              },
            }}
            exit={{
              opacity: 0,
              scale: 0.95,
              transition: {
                duration: ANIMATION_EXIT_TIME_SECONDS,
              },
            }}
          >
            {children}
          </Dialog.Panel>
        </div>
      </div>
    </Dialog>
  );
};

function ModalTitle({
  className,
  children,
}: {
  className?: string;
  children: React.ReactNode;
}) {
  return (
    <h3
      className={twMerge(
        "my-2 text-lg font-semibold leading-6 text-gray-900",
        className,
      )}
    >
      {children}
    </h3>
  );
}

Modal.Title = ModalTitle;

export default Modal;
